import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * Custom hook for managing URL search parameters
 * Provides methods to get, set, and delete query params while syncing with browser history
 *
 * @example
 * const { getParam, setParam, deleteParam } = useUrlParam();
 *
 * // Get a param value
 * const docketId = getParam('docket');
 *
 * // Set a param
 * setParam('docket', '12345');
 *
 * // Delete a param
 * deleteParam('docket');
 */
export const useUrlParam = () => {
  const history = useHistory();
  const location = useLocation();

  /**
   * Get a parameter value from URL search params
   * @param {string} key - The parameter key to retrieve
   * @returns {string|null} The parameter value or null if not found
   */
  const getParam = useCallback(
    key => {
      const sp = new URLSearchParams(location.search);
      return sp.get(key);
    },
    [location.search],
  );

  /**
   * Set a parameter in URL search params
   * @param {string} key - The parameter key
   * @param {string} value - The parameter value
   */
  const setParam = useCallback(
    (key, value) => {
      const sp = new URLSearchParams(location.search);
      sp.set(key, value);
      history.push({ pathname: location.pathname, search: sp.toString() });
    },
    [history, location.pathname, location.search],
  );

  /**
   * Delete a parameter from URL search params
   * @param {string} key - The parameter key to remove
   */
  const deleteParam = useCallback(
    key => {
      const sp = new URLSearchParams(location.search);
      sp.delete(key);
      history.push({ pathname: location.pathname, search: sp.toString() });
    },
    [history, location.pathname, location.search],
  );

  /**
   * Set multiple parameters at once
   * @param {Object} params - Object with key-value pairs to set
   */
  const setParams = useCallback(
    params => {
      const sp = new URLSearchParams(location.search);
      Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          sp.delete(key);
        } else {
          sp.set(key, value);
        }
      });
      history.replace({ pathname: location.pathname, search: sp.toString() });
    },
    [history, location.pathname, location.search],
  );

  /**
   * Delete multiple parameters at once
   * @param {string[]} keys - Array of parameter keys to remove
   */
  const deleteParams = useCallback(
    keys => {
      const sp = new URLSearchParams(location.search);
      keys.forEach(key => sp.delete(key));
      history.replace({ pathname: location.pathname, search: sp.toString() });
    },
    [history, location.pathname, location.search],
  );

  /**
   * Get all search params as an object
   * @returns {Object} Object containing all search params
   */
  const getAllParams = useCallback(() => {
    const sp = new URLSearchParams(location.search);
    return Object.fromEntries(sp.entries());
  }, [location.search]);

  /**
   * Clear all search params
   */
  const clearAllParams = useCallback(() => {
    history.replace({ pathname: location.pathname, search: '' });
  }, [history, location.pathname]);

  return {
    getParam,
    setParam,
    deleteParam,
    setParams,
    deleteParams,
    getAllParams,
    clearAllParams,
  };
};

export default useUrlParam;
